import {
    Alert, Button, Grid, Snackbar, Stack, Typography, Modal, Paper,
    TextField, Box, Switch, Tooltip, IconButton, CircularProgress, Divider
} from "@mui/material";
import KeyboardArrowLeftTwoToneIcon from "@mui/icons-material/KeyboardArrowLeftTwoTone";
import MainCard from "../../components/cards/MainCard";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from 'react-redux';
import SimulationCollectionForm from "./SimulationCollectionForm";
import {
    useCreateSimulationCollectionMutation,
    useGetSimulationCollectionByIdQuery,
    useUpdateSimulationCollectionMutation
} from "../../store/services/simulation-service";
import { useState, useEffect, useMemo } from "react";
import SimulationSkeletonComponent from "../../components/Skeleton/SimulationSkeletonComponent";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { getStatusMessage } from "../../utils/getStatusMessage";
import TenancySelectionComponent from "../../components/TenancySelectionComponent";
import { useTheme } from "@mui/material/styles";
import { getCardColor } from "../../utils/colorUtils";
import HelpIcon from "@mui/icons-material/HelpOutline";
import SettingsIcon from '@mui/icons-material/Settings';
import ConfirmationDialog from "../../utils/confirmationDialog";


export default function SimulationCollectionDetails() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const theme = useTheme();
    const lightMode = theme.palette.mode === "light";

    const [createCollection] = useCreateSimulationCollectionMutation();
    const [updateCollection] = useUpdateSimulationCollectionMutation();
    const {
        data: collectionData,
        isLoading,
        error: isError
    } = useGetSimulationCollectionByIdQuery(id, { skip: !isEditMode });

    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [isSaving, setIsSaving] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogAction, setDialogAction] = useState(null);

    // Modal and form state
    const [nameModalOpen, setNameModalOpen] = useState(!isEditMode);
    const [collectionName, setCollectionName] = useState('');
    const [nameError, setNameError] = useState('');
    const [selectedSimulations, setSelectedSimulations] = useState([]);
    const [sharedTenancyIds, setSharedTenancyIds] = useState([]);
    const [publishToFamily, setPublishToFamily] = useState(false);
    const [published, setPublished] = useState(false);

    const { tenancyId } = useSelector((state) => state.tenancy);

    const user = JSON.parse(localStorage.getItem("user"));
    const isAdmin = user.role === "Administrators";

    useEffect(() => {
        if (isEditMode && collectionData) {
            setCollectionName(collectionData.collectionName);
            setSelectedSimulations(collectionData.simulationIds || []);
            setSharedTenancyIds(collectionData.sharedTenancyIds || []);
            setPublishToFamily(collectionData.publishToFamily || false);
            setPublished(collectionData.published || false);
        } else if (!isAdmin && !isEditMode) {
            // Use tenancyId from Redux instead of user.tenancyId
            setSharedTenancyIds([tenancyId]);
            setPublished(true);
        }
    }, [isEditMode, collectionData, isAdmin, tenancyId]); // Update dependency array

    // Memoized initialData like content packs - this is the key fix
    const initialData = useMemo(() => {
        if (isEditMode) {
            if (isLoading) {
                return null; // Don't render form while loading
            }
            return {
                simulationIds: collectionData?.simulationIds || [],
                collectionName: collectionData?.collectionName || ''
            };
        }
        return {
            simulationIds: selectedSimulations,
            collectionName: collectionName
        };
    }, [isEditMode, collectionData, selectedSimulations, collectionName, isLoading]);

    const handleNavigateBack = () => {
        if (isSaving) {
            setDialogOpen(true);
            setDialogAction(() => () => navigate('/tenancy-simulations?tab=1'));
            return;
        }
        navigate('/tenancy-simulations?tab=1');
    };

    const handleNameSubmit = () => {
        if (!collectionName.trim()) {
            setNameError('Collection Name is required');
            return;
        }
        setNameModalOpen(false);
    };

    const handleEditDetails = () => {
        setNameModalOpen(true);
    };

    const handleFormSave = (data) => {
        setSelectedSimulations(data.simulationIds);
    };

    const handleSharedTenanciesChange = (tenancyIds) => {
        setSharedTenancyIds(tenancyIds);
    };

    const handlePublishToFamilyChange = (event) => {
        setPublishToFamily(event.target.checked);
    };

    const handlePublishedChange = (event) => {
        setPublished(event.target.checked);
    };

    const handleSaveCollection = async () => {
        if (!collectionName.trim() && !isEditMode) {
            setSnackbarSeverity("error");
            setSnackbarMessage("Collection Name is required");
            setSnackbarOpen(true);
            return;
        }

        if (selectedSimulations.length === 0) {
            setSnackbarSeverity("error");
            setSnackbarMessage("Please select at least one simulation");
            setSnackbarOpen(true);
            return;
        }

        setIsSaving(true);
        try {
            const transformedData = isEditMode ? {
                name: collectionName,
                published: isAdmin ? published : (collectionData.published || true),
                simulationIds: selectedSimulations,
                // Use tenancyId from Redux instead of user.tenancyId
                sharedTenancyIds: isAdmin ? sharedTenancyIds : (collectionData.sharedTenancyIds || [tenancyId]),
                publishToFamily: publishToFamily
            } : {
                name: collectionName,
                published: isAdmin ? published : true,
                simulationIds: selectedSimulations,
                // Use tenancyId from Redux instead of user.tenancyId
                sharedTenancyIds: isAdmin ? sharedTenancyIds : [tenancyId],
                publishToFamily: publishToFamily
            };

            const result = isEditMode
                ? await updateCollection({
                    collectionId: id,
                    collectionData: transformedData
                }).unwrap()
                : await createCollection(transformedData).unwrap();

            setSnackbarSeverity("success");
            setSnackbarMessage(`Collection ${isEditMode ? 'updated' : 'created'} successfully!`);
            setSnackbarOpen(true);
            setIsSaving(false);

            await new Promise(resolve => setTimeout(resolve, 500));
            navigate('/tenancy-simulations?tab=1'); 

        } catch (error) {
            console.error('Error saving collection:', error);
            const errorMessage = error.data?.message ||
                `Failed to ${isEditMode ? 'update' : 'create'} collection`;

            setSnackbarSeverity("error");
            setSnackbarMessage(errorMessage);
            setSnackbarOpen(true);
            setIsSaving(false);
        }
    };

    if (isLoading && isEditMode) return <SimulationSkeletonComponent />;

    if (!isLoading && isError) {
        return (
            <MainCard boxShadow={true} border={false} sx={{ minHeight: "calc(100vh - 200px)", display: "flex", justifyContent: "center", alignItems: "center", bgcolor: 'background.default' }}>
                <Grid container spacing={2} justifyContent="center" alignItems="center">
                    <Grid item xs={12} md={8}>
                        <Stack direction="column" spacing={4} alignItems="center">
                            <ErrorOutlineIcon color="error" style={{ fontSize: 80 }} />
                            <Typography variant="h3" align="center" color="textPrimary">
                                {getStatusMessage(isError)}
                            </Typography>
                            <Typography variant="body1" align="center" color="textSecondary">
                                Please refresh the page or contact the administrator.
                            </Typography>
                            <Button onClick={() => navigate(-1)} variant="contained" startIcon={<KeyboardArrowLeftTwoToneIcon />}>
                                Back
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </MainCard>
        );
    }

    return (
        <Box sx={{
            width: '100%',
            height: '100%',
            overflow: 'auto'
        }}>
            <Grid container rowSpacing={2.5} columnSpacing={2.75}>
                <Grid item xs={12}>
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Button
                            onClick={handleNavigateBack}
                            variant="contained"
                            startIcon={<KeyboardArrowLeftTwoToneIcon />}
                        >
                            Back
                        </Button>
                        {!nameModalOpen && (
                            <Button
                                variant="contained"
                                color="warning"
                                onClick={handleSaveCollection}
                                disabled={(!collectionName && !isEditMode) || selectedSimulations.length === 0 || isSaving}
                            >
                                {isSaving ? (
                                    <>
                                        <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                                        {isEditMode ? 'Updating...' : 'Creating...'}
                                    </>
                                ) : (
                                    isEditMode ? 'Update Collection' : 'Create Collection'
                                )}
                            </Button>
                        )}
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container justifyContent="space-between" sx={{ mb: 4 }}>
                        <Box display="flex" alignItems="center" width="100%">
                            <Typography variant="h2" mb={2}>
                                {isEditMode
                                    ? collectionData
                                        ? `${collectionData.collectionName}`
                                        : "Loading..."
                                    : `${collectionName ? `${collectionName}` : ''}`}
                            </Typography>
                            {!nameModalOpen && (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleEditDetails}
                                    endIcon={<SettingsIcon />}
                                    sx={{ ml: 3, mb: 2, height: 40 }}
                                >
                                    Edit Details
                                </Button>
                            )}
                        </Box>
                        <Divider sx={{ width: "100%" }} />
                    </Grid>
                    {/* Only render form when initialData is available */}
                    {initialData && (
                        <SimulationCollectionForm
                            initialData={initialData}
                            onSave={handleFormSave}
                            mode={isEditMode ? "edit" : "add"}
                            isSaving={isSaving}
                        />
                    )}
                </Grid>
            </Grid>

            {/* Name and Settings Modal */}
            <Modal
                open={nameModalOpen}
                disableEscapeKeyDown
                disableBackdropClick
                aria-labelledby="collection-details-modal"
            >
                <Paper sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    borderRadius: '8px',
                    width: 600,
                    p: 4,
                    outline: 'none',
                    backgroundColor: 'background.default',
                    maxHeight: '90vh',
                    overflow: 'auto'
                }}>
                    <Typography variant="h6" component="h2" gutterBottom>
                        Collection Details
                    </Typography>

                    {/* Collection Name */}
                    <TextField
                        fullWidth
                        label="Collection Name"
                        value={collectionName}
                        disabled={isEditMode}
                        onChange={(e) => {
                            setCollectionName(e.target.value);
                            if (e.target.value.trim()) setNameError('');
                        }}
                        error={!!nameError}
                        helperText={nameError}
                        sx={{ mb: 3 }}
                    />

                    {/* Visibility Card - only for admins */}
                    {isAdmin && (
                        <MainCard
                            content={true}
                            boxShadow={true}
                            border={false}
                            sx={{
                                mb: 3,
                                bgcolor: getCardColor(lightMode, published)
                            }}
                        >
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Typography variant="h4">Collection Visibility</Typography>
                                    <Tooltip title="When enabled, the collection will be visible to users according to sharing settings" placement="top">
                                        <IconButton size="small">
                                            <HelpIcon sx={{ color: "secondary.main" }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Switch
                                    checked={published}
                                    onChange={handlePublishedChange}
                                    inputProps={{ "aria-label": "collection visibility" }}
                                />
                            </Box>
                            <Typography variant="caption">
                                {published
                                    ? "The collection is visible to users according to sharing settings"
                                    : "The collection is currently hidden from all users"}
                            </Typography>
                        </MainCard>
                    )}

                    {/* Collection Sharing Settings Card */}
                    {isAdmin && (
                        <MainCard
                            content={true}
                            boxShadow={true}
                            border={false}
                            sx={{ mb: 3, bgcolor: getCardColor(lightMode) }}
                        >
                            <Typography variant="h4" sx={{ mb: 2 }}>Collection Sharing Settings</Typography>

                            <TenancySelectionComponent
                                selectedTenancies={sharedTenancyIds || []}
                                onChange={handleSharedTenanciesChange}
                            />
                        </MainCard>
                    )}

                    <MainCard
                        content={true}
                        boxShadow={true}
                        border={false}
                        sx={{
                            mb: 3,
                            bgcolor: getCardColor(lightMode)
                        }}
                    >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Typography variant="body1">Share with Tenancy Family</Typography>
                                <Tooltip title="When enabled, this collection will be accessible to all descendants of the selected tenancies" placement="top">
                                    <IconButton size="small">
                                        <HelpIcon sx={{ color: "secondary.main" }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Switch
                                checked={publishToFamily || false}
                                onChange={handlePublishToFamilyChange}
                                inputProps={{ "aria-label": "publish to family" }}
                                disabled={sharedTenancyIds?.includes(0)}
                            />
                        </Box>
                        {sharedTenancyIds?.includes(0) && (
                            <Typography variant="caption">
                                "Family sharing" is disabled because the collection is shared with all tenancies.
                            </Typography>
                        )}
                    </MainCard>

                    <Button
                        variant="contained"
                        onClick={handleNameSubmit}
                        disabled={!collectionName.trim()}
                    >
                        {isEditMode ? 'Confirm' : 'Continue'}
                    </Button>
                </Paper>
            </Modal>

            <Snackbar anchorOrigin={{ vertical: "top", horizontal: "center" }} open={snackbarOpen} autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}>
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            <ConfirmationDialog
                open={dialogOpen}
                title="Unsaved Changes"
                content="You have unsaved changes. Are you sure you want to leave?"
                onConfirm={() => {
                    setDialogOpen(false);
                    dialogAction();
                }}
                onCancel={() => setDialogOpen(false)}
            />
        </Box>
    );
};